import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Search, Zap, Shield } from "lucide-react";
import PageShell from "../components/PageShell";
import { provisionalScore } from "../lib/api";

const PLACEHOLDER_IDEAS = [
  "A subscription box of small-batch hot sauces for spice nerds",
  "A SaaS that auto-generates LinkedIn carousels from blog posts",
  "Window washing for small commercial buildings in my city",
  "An app that scores Airbnb listings before you book",
  "Custom embroidered hats for craft breweries",
  "A directory of dog-friendly coworking spaces",
  "A weekly newsletter teaching solopreneurs how to do their own taxes",
  "AI-generated bedtime stories with your kid's name in them",
  "Done-for-you Etsy listings for handmade soap sellers",
  "Mobile detailing that only services electric vehicles",
];

function useTypewriterPlaceholder(items, { typeMs = 42, pauseMs = 1500, deleteMs = 22, disabled = false } = {}) {
  const [text, setText] = useState("");

  useEffect(() => {
    if (disabled) {
      setText("");
      return undefined;
    }
    let i = 0;
    let phase = "typing";
    let charIdx = 0;
    let timer;

    const tick = () => {
      const current = items[i % items.length];
      if (phase === "typing") {
        charIdx += 1;
        setText(current.slice(0, charIdx));
        if (charIdx >= current.length) {
          phase = "pausing";
          timer = setTimeout(tick, pauseMs);
        } else {
          timer = setTimeout(tick, typeMs);
        }
      } else if (phase === "pausing") {
        phase = "deleting";
        timer = setTimeout(tick, deleteMs);
      } else {
        charIdx -= 1;
        setText(current.slice(0, charIdx));
        if (charIdx <= 0) {
          i += 1;
          phase = "typing";
          timer = setTimeout(tick, typeMs * 4);
        } else {
          timer = setTimeout(tick, deleteMs);
        }
      }
    };
    timer = setTimeout(tick, 400);
    return () => clearTimeout(timer);
  }, [items, typeMs, pauseMs, deleteMs, disabled]);

  return text;
}

function Words({ text, baseDelay = 0, perWordMs = 70 }) {
  return text.split(" ").map((w, i) => (
    <span
      key={`${w}-${i}`}
      className="gl-word"
      style={{ animationDelay: `${baseDelay + i * perWordMs}ms` }}
    >
      {w}
      {i < text.split(" ").length - 1 ? "\u00A0" : ""}
    </span>
  ));
}

export default function Landing() {
  const navigate = useNavigate();
  const [idea, setIdea] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const placeholder = useTypewriterPlaceholder(PLACEHOLDER_IDEAS, {
    disabled: idea.length > 0 || loading,
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = idea.trim();
    if (trimmed.length < 6) {
      setError("Type your idea in one sentence (at least 6 characters).");
      return;
    }
    setLoading(true);
    try {
      const res = await provisionalScore(trimmed);
      navigate("/result", {
        state: {
          run_id: res.run_id,
          provisional_score: res.provisional_score,
          verdict_line: res.verdict_line,
          category: res.category,
          idea_text: trimmed,
        },
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Could not score that right now. Please try again.";
      setError(typeof msg === "string" ? msg : "Could not score that right now.");
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-10 relative">
        <div className="relative text-center">
          <div
            className="gl-reveal inline-flex items-center gap-2 rounded-full border border-[#4ADE80]/30 bg-[#4ADE80]/10 px-3 py-1 mb-7"
            style={{ animationDelay: "60ms" }}
          >
            <span className="gl-pulse-dot" />
            <span className="text-[11px] tracking-[0.18em] uppercase text-[#4ADE80] font-bold">
              Free Idea Validation
            </span>
          </div>

          <h1
            className="gl-hero-heading text-white"
            style={{ fontSize: "clamp(28px, 7vw, 48px)" }}
          >
            <Words text="Score your idea" baseDelay={250} perWordMs={80} />
            <br />
            <span className="text-[#4ADE80]">
              <Words text="honestly," baseDelay={550} perWordMs={80} />
            </span>
            <Words text=" in 90 seconds." baseDelay={700} perWordMs={80} />
          </h1>
        </div>

        <form
          onSubmit={onSubmit}
          className="gl-reveal mt-12 relative"
          style={{ animationDelay: "1300ms" }}
          data-testid="idea-form"
        >
          <div className="gl-card p-5 sm:p-6 gl-card-active">
            <label htmlFor="idea" className="gl-label block mb-3 px-2">
              Your idea, one sentence
            </label>
            <div className="relative">
              <input
                id="idea"
                data-testid="idea-input"
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
                maxLength={500}
                className="gl-input pr-14 text-base sm:text-lg relative bg-transparent"
                autoFocus
                disabled={loading}
                placeholder=""
              />
              {/* Animated placeholder overlay (visible only when input is empty) */}
              {idea.length === 0 ? (
                <div
                  className="absolute inset-0 flex items-center pl-5 sm:pl-6 pr-14 pointer-events-none text-base sm:text-lg text-[#6c7770] truncate"
                  data-testid="idea-placeholder"
                >
                  <span className="truncate">{placeholder}</span>
                  <span className="ml-px w-[2px] h-5 bg-[#4ADE80] gl-caret" />
                </div>
              ) : null}
              <Search
                size={18}
                className="absolute right-6 top-1/2 -translate-y-1/2 text-[#6c7770]"
              />
            </div>
            {error ? (
              <div className="text-sm text-red-400 mt-3 px-2" data-testid="idea-error">
                {error}
              </div>
            ) : null}
            <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <button
                type="submit"
                disabled={loading}
                data-testid="submit-idea-button"
                className="gl-cta text-base sm:text-lg gl-cta-block sm:w-auto"
              >
                {loading ? "Scoring..." : "Score my idea free"}
                {!loading && <ArrowRight size={18} />}
              </button>
              <button
                type="button"
                onClick={() =>
                  window.open(
                    "https://trylightbulb.com/offer",
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                className="text-sm text-[#b6c1b8] hover:text-[#4ADE80] transition-colors text-left sm:text-right"
                data-testid="no-idea-link"
              >
                No idea yet? Try the Lightbulb Idea Engine →
              </button>
            </div>
          </div>
        </form>

        <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-3 mt-10">
          {[
            { icon: Search, label: "Real Research", body: "We pull from Reddit, YouTube, market reports, and franchise filings. Real operators, real numbers, not vibes." },
            { icon: Zap, label: "Transparent Score", body: "Demand, proof, monetization, feasibility, competition, edge. See exactly how you scored." },
            { icon: Shield, label: "Sources Linked", body: "Every figure links to where we found it. Verify any of it yourself." },
          ].map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.label}
                className="gl-reveal gl-card-lift p-5"
                style={{ animationDelay: `${1500 + i * 120}ms` }}
              >
                <Icon size={18} className="text-[#4ADE80] mb-3" />
                <div className="gl-label mb-1">{f.label}</div>
                <div className="text-sm text-[#b6c1b8] leading-relaxed">{f.body}</div>
              </div>
            );
          })}
        </div>
      </section>
    </PageShell>
  );
}
